import { afterNextPaint } from '@helpers/after-next-paint';
import { DISTANCE_FILTER_ID } from '@pages/VendorsSearch/containers/FilterPills/atoms';
import {
	DEFAULT_ALL_RADIO_OPTION_ID,
	defaultOption,
} from '@pages/VendorsSearch/containers/FilterPills/components/facet-single-select-filter';
import { TextCaption } from '@tkww/orion-web-ds-text';
import {
	RadioButton,
	SingleSelectGroup,
} from '@xo-union/tk-component-switches';
import React, {
	memo,
	useMemo,
	type ChangeEvent,
	type FC,
	useCallback,
} from 'react';
import { subtext, toSidebarOptionCase } from '../../../utils';
import { useSelectRadioSidebarFilter } from '../../hooks/use-select-radio-sidebar-filter';
import type { OptionProps } from '../../types';
import Styles from '../SidebarOptions/styles.scss';

interface SingleSelectOption {
	name: string;
	value: string;
	subtext?: string;
}

const SingleOptionComponent: FC<OptionProps> = ({
	filter,
	selectedSidebarFilters,
	isFilterPolishEnabled,
}) => {
	const selectRadioSidebarFilter = useSelectRadioSidebarFilter();

	const selectedFilterId = useMemo(() => {
		return filter.filterOptions.find((option) =>
			selectedSidebarFilters.includes(option.id),
		)?.id;
	}, [filter.filterOptions, selectedSidebarFilters]);

	const shouldSort = isFilterPolishEnabled && filter.id !== DISTANCE_FILTER_ID;

	const options = useMemo(() => {
		const sortedFilterOptions = shouldSort
			? [...filter.filterOptions].sort((a, b) =>
					a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
				)
			: filter?.filterOptions;
		const filterOptions: SingleSelectOption[] =
			sortedFilterOptions?.map((o) => ({
				name: o.name,
				value: o.id,
				subtext: subtext[o.id as keyof typeof subtext],
			})) || [];
		const normalizedDefaultOption: SingleSelectOption = {
			name: defaultOption.name,
			value: defaultOption.value,
		};
		return filter?.hideAllOption
			? filterOptions
			: [normalizedDefaultOption, ...filterOptions];
	}, [filter?.filterOptions, filter?.hideAllOption, shouldSort]);

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			afterNextPaint(() => {
				const { value } = e.target;
				selectRadioSidebarFilter(value, selectedFilterId);
			});
		},
		[selectedFilterId, selectRadioSidebarFilter],
	);

	return (
		<SingleSelectGroup>
			{options.map((option) => {
				const { value, subtext, name } = option;
				const isSelected = selectedFilterId === value;
				const isAllOption = value === DEFAULT_ALL_RADIO_OPTION_ID;
				const checked = isSelected || (!selectedFilterId && isAllOption);
				return (
					<div className={Styles.option} key={`option_${value}`}>
						<RadioButton value={value} checked={checked} onChange={onChange}>
							{isFilterPolishEnabled ? toSidebarOptionCase(name) : name}
							{subtext && (
								<TextCaption className={Styles.caption}>{subtext}</TextCaption>
							)}
						</RadioButton>
					</div>
				);
			})}
		</SingleSelectGroup>
	);
};

const getSelectedFilterId = (
	filterOptionIds: string[],
	selectedSidebarFilters: string[],
): string | undefined => {
	return filterOptionIds.find((optionId) =>
		selectedSidebarFilters.includes(optionId),
	);
};

export const SingleOption = memo(
	SingleOptionComponent,
	(prevProps, nextProps) => {
		if (prevProps.filter !== nextProps.filter) {
			return false;
		}
		if (prevProps.isFilterPolishEnabled !== nextProps.isFilterPolishEnabled) {
			return false;
		}

		if (prevProps.selectedSidebarFilters === nextProps.selectedSidebarFilters) {
			return true;
		}

		const filterOptionIds = nextProps.filter.filterOptions.map(
			(option) => option.id,
		);
		const prevSelectedFilterId = getSelectedFilterId(
			filterOptionIds,
			prevProps.selectedSidebarFilters,
		);
		const nextSelectedFilterId = getSelectedFilterId(
			filterOptionIds,
			nextProps.selectedSidebarFilters,
		);

		return prevSelectedFilterId === nextSelectedFilterId;
	},
);
